import { formBuilderData } from "../interface/response";
import { cloneData, isArray, isEmptyObj } from "./utilityHelper";

interface MapCol {
    [key: string]: string | Partial<formBuilderData> | formBuilderData;
}

interface FormTransformOption {
    map?: MapCol | ((item: formBuilderData) => any);
    skip?: string[];
    replace?: { [key: string]: formBuilderData };
    shift?: { data: (string | number | formBuilderData)[]; place: 'AFTER' | 'BEFORE' | 'INITIAL' | 'LAST'; pos?: string | number };
    merge?: formBuilderData[] | Array<formBuilderData[]>;
    remove?: string | string[];
    filter?: ((item: formBuilderData) => boolean) | string[]; // Add the filter property
    splice?: { start: number; deleteCount: number }, // Added splice property
    transform?: (item: formBuilderData) => any
}

export function formTransform(f: formBuilderData[], options: FormTransformOption = {}): formBuilderData[] {
    const formData = new FormDataManipulation(f);

    for (const [key, value] of Object.entries(options)) {
        if (typeof formData[key] === 'function') {
            // Special handling for filter
            if (['filter', 'transform', 'map'].includes(key) && typeof value === 'function') {
                formData[key](value);
            } else if (['filter', 'merge'].includes(key) && Array.isArray(value)) {
                //@ts-ignore
                formData[key](value);
            } else if (key === 'skip' && Array.isArray(value)) {
                formData[key](value as string[]);
            } else if (key === 'splice' && typeof value === 'object' && 'start' in value && 'deleteCount' in value) {
                formData[key](value.start, value.deleteCount);
            }
            else if (key === 'shift' && typeof value === 'object' && 'data' in value && 'place' in value) {
                formData[key](value.data, value.place, value.pos);
            }
            else {
                formData[key](...(Array.isArray(value) ? value : [value]));
            }
        }
    }

    return formData.getFormData();
}


export class FormDataManipulation {
    private formData: formBuilderData[];

    constructor(data: formBuilderData[]) {
        this.formData = cloneData(data);
    }

    setFormData(d: formBuilderData[]) {
        this.formData = [...cloneData(d)];
    }
    private add(d: formBuilderData[]) {
        if (isArray(d)) {
            this.formData = [...this.formData, ...d]
        }
    }

    /**
   * Maps column names in formData based on the provided columnMapping.
   * @param columnMapping - A mapping of old column names to new column names.
   * @returns The updated formData array with mapped column names.
   */
    map(columnMapping: (item: formBuilderData) => any | MapCol): formBuilderData[] {
        if (typeof columnMapping === 'function') {
            this.formData = this.formData.map(columnMapping);
        } else {
            this.formData = this.formData.map(item => {
                const newColName: any = columnMapping[item.colName];
                if (typeof newColName === 'string') {
                    // Replace colName if a string is provided in the mapping
                    return { ...item, colName: newColName };
                } else if (typeof newColName === 'object' && newColName !== null) {
                    // Merge with the object if an object is provided in the mapping
                    return { ...item, ...newColName };
                }
                // Return the item unchanged if no valid mapping is found
                return item;
            });
        }
        return this.formData;
    }

    transform(predicate: (item: formBuilderData) => boolean | string[]) {
        this.formData.map(predicate);
    }

    /**
  * Splice removes elements from the beginning of the formData array.
  * @param start - The start index to begin removal.
  * @param deleteCount - The number of elements to remove.
  * @returns The updated formData array.
  */
    splice(start: number, deleteCount: number) {
        this.formData.splice(start, deleteCount);
        return this.formData;
    }


    filter(predicate: (item: formBuilderData) => boolean | string[]): formBuilderData[] {
        if (typeof predicate === 'function') {
            this.formData = this.formData.filter(predicate);
        } else if (Array.isArray(predicate)) {
            const columnSet = new Set(predicate);
            this.formData = this.formData.filter(item => columnSet.has(item.colName));
        }
        // this.formData = this.formData.filter(predicate);
        return this.formData;
    }

    replace(replacements: { [key: string]: formBuilderData }, fully: boolean = false) {
        this.formData = this.formData.map(item => {
            if (replacements[item.colName]) {
                return fully ? replacements[item.colName] : { ...item, ...replacements[item.colName] };
            }
            return item;
        });
        return this.formData;
    }

    skip(columns: string[]) {
        const columnSet = new Set(columns); // Use a Set for faster lookups
        this.formData = this.formData.filter(item => !columnSet.has(item.colName)); // Filter out items whose colName is in columnSet
        return this.formData;
    }

    // shift(data: (string | number)[], place: 'AFTER' | 'BEFORE' | 'INITIAL' | 'LAST', pos: string | number = 0) {
    //     const indices = data.map(d => (typeof d === 'number' ? d : this.findIndex(d)));
    //     if (indices.some(i => i === -1)) throw new Error('One or more items to shift were not found.');

    //     const moveItems = (from: number[], to: number, insert: (item: formBuilderData, index: number) => void) => {
    //         from.forEach(index => {
    //             const [item] = this.formData.splice(index, 1);
    //             insert(item, to);
    //         });
    //     };

    //     switch (place) {
    //         case 'AFTER':
    //             const afterIndex = typeof pos === 'number' ? pos : this.findIndex(pos);
    //             if (afterIndex !== -1) {
    //                 moveItems(indices, afterIndex + 1, (item, index) => this.formData.splice(index, 0, item));
    //             }
    //             break;

    //         case 'BEFORE':
    //             const beforeIndex = typeof pos === 'number' ? pos : this.findIndex(pos);
    //             if (beforeIndex !== -1) {
    //                 moveItems(indices, beforeIndex, (item, index) => this.formData.splice(index, 0, item));
    //             }
    //             break;

    //         case 'INITIAL':
    //             moveItems(indices, 0, (item, index) => this.formData.unshift(item));
    //             break;
    //         case 'LAST':
    //             moveItems(indices, this.formData.length, (item, index) => this.formData.push(item));
    //             break;
    //         default:
    //             throw new Error('Invalid place parameter.');
    //     }
    //     return this.formData;
    // }

    shift(data: (string | number | formBuilderData)[], place: 'AFTER' | 'BEFORE' | 'INITIAL' | 'LAST', pos: string | number = 0) {
        const existingIndices: number[] = [];
        const newItems: formBuilderData[] = [];
        console.log('shift data', data)

        data.forEach(d => {
            if (typeof d === 'string' || typeof d === 'number') {
                // If it's a string/number, assume it's a colName and find its index
                const index = this.findIndex(d);
                if (index !== -1) existingIndices.push(index);
            } else if (d.colName) {
                // If it's an object with colName, check if it exists in formData
                const index = this.findIndex(d.colName);
                if (index !== -1) {
                    existingIndices.push(index); // Treat as existing item to move
                } else {
                    newItems.push(d); // Treat as new item to insert
                }
            }
        });
        console.log('Existing Indices:', existingIndices);
        console.log('New Items:', newItems);

        // Function to move existing indices
        const moveItems = (from: number[], to: number, insert: (item: formBuilderData, index: number) => void) => {
            from.forEach(index => {
                const [item] = this.formData.splice(index, 1);
                insert(item, to);
            });
        };

        switch (place) {
            case 'AFTER':
            case 'BEFORE':
                const refIndex = typeof pos === 'number' ? pos : this.findIndex(pos);
                if (refIndex !== -1) {
                    moveItems(existingIndices, place === 'AFTER' ? refIndex + 1 : refIndex, (item, index) => this.formData.splice(index, 0, item));
                    newItems.forEach(item => this.formData.splice(place === 'AFTER' ? refIndex + 1 : refIndex, 0, item));
                }
                break;

            case 'INITIAL':
                moveItems(existingIndices, 0, (item, index) => this.formData.unshift(item));
                newItems.forEach(item => this.formData.unshift(item));
                break;

            case 'LAST':
                moveItems(existingIndices, this.formData.length, (item, index) => this.formData.push(item));
                newItems.forEach(item => this.formData.push(item));
                break;

            default:
                throw new Error('Invalid place parameter.');
        }
        return this.formData;
    }

    findIndex(col: string | number): number {
        return typeof col === 'number' ? col : this.formData.findIndex(item => item.colName === col);
    }

    merge(newData: formBuilderData[] | Array<formBuilderData[]>) {
        if (Array.isArray(newData) && Array.isArray(newData[0])) {
            // It's Array<formBuilderData[]>
            const flattened = (newData as formBuilderData[][]).flat();
            this.add(flattened);
        } else {
            // It's formBuilderData[]
            this.add(newData as formBuilderData[]);
        }
        return this.getFormData();
    }
    template(temp: Array<string> | Array<{ colName: string, title: string }>) {
        const template: formBuilderData[] = [];
        temp.forEach((element: any) => {
            if (typeof element == 'string') {

            }
        });
        this.setFormData(template);
        return this.formData;
    }

    remove(columns: string | string[]) {
        const columnArray = Array.isArray(columns) ? columns : [columns];
        const columnSet = new Set(columnArray);
        this.formData = this.formData.filter(item => !columnSet.has(item.colName));
        return this.formData;
    }

    getFormData() {
        return this.formData;
    }
}
